import { HttpRequest } from "../utils";

const httpRequest = new HttpRequest();

export type AccountScope = "SYSTEM" | "CONTROL" | "SUBSIDIARY";
export type NormalBalance = "DEBIT" | "CREDIT";

export interface ILedgerAccount {
  accountId: string;
  code: string;
  name: string;
  role: string;
  scope: AccountScope;
  accountType: string;
  normalBalance: NormalBalance;
  /** Дэд данс нэгтгэх данс руугаа заана. Зөвхөн RECV / INV / PAYABLE-д. */
  parentId: string | null;
  debit: number;
  credit: number;
  /** Хэвийн үлдэгдлийн тэмдгээр: эерэг = хэвийн тал. */
  balance: number;
}

export interface IControlCheck {
  code: string;
  role: string;
  directBalance: number;
  subsidiaryTotal: number;
  subsidiaryCount: number;
  effectiveBalance: number;
  /** 0-ээс өөр бол дэд дансыг алгасаж шууд бичсэн байна. */
  diff: number;
}

export interface IARReconciliation {
  businessId: string;
  recvControl: number;
  arSubLedger: number;
  diff: number;
}

export interface IAccountsResponse {
  businessId: string;
  accounts: ILedgerAccount[];
  controls: IControlCheck[];
  totalDebit: number;
  totalCredit: number;
  balanced: boolean;
  ar: IARReconciliation | null;
}

export interface IPostingTemplateLine {
  role: string;
  side: "DEBIT" | "CREDIT";
  amount: string;
  skipIfZero?: boolean;
  description?: string;
}

export interface IPostingTemplate {
  id: string;
  code: string;
  name: string;
  event: string;
  isActive: boolean;
  /** null = бүх бизнест хамаарах default дүрэм. */
  businessId: string | null;
  lines: IPostingTemplateLine[];
}

export interface IJournalEntry {
  id: string;
  entryNumber: string;
  entryDate: string;
  sourceType: string;
  sourceId: string | null;
  postingTemplate: string | null;
  description: string | null;
  totalDebit: number;
  totalCredit: number;
  status: "POSTED" | "REVERSED";
  reversalOfId: string | null;
  /** Бичилтийн мөрийн тоо (listJournal). */
  lineCount?: number;
  /** Дебит / кредит талын дансны кодууд («1200.BU-100182» → «1200»). */
  accountsDebit?: string[];
  accountsCredit?: string[];
  /** Богиносгоогүй кодууд — tooltip-д. */
  accountsDebitFull?: string[];
  accountsCreditFull?: string[];
  /** Эх нэхэмжлэх цуцлагдсан боловч бичилт урвуулагдаагүй. */
  alert?: "SOURCE_CANCELLED_NOT_REVERSED";
}

export interface IJournalLine {
  id: string;
  lineNumber: number;
  accountId: string;
  accountCode: string;
  accountName: string;
  isDebit: boolean;
  amount: number;
  description: string | null;
}

export interface IJournalEntryRef {
  id: string;
  entryNumber: string;
}

export type IJournalEntryDetail = IJournalEntry & {
  lines: IJournalLine[];
  createdAt: string;
  /** Энэ бичилтийг урвуулсан бичилт. */
  reversedBy: IJournalEntryRef | null;
  /** Энэ бичилт урвуулж буй эх бичилт. */
  reversalOf: IJournalEntryRef | null;
  /** Эх баримт: нэхэмжлэх (эсвэл залруулгын нэхэмжлэх). */
  source: { invoiceId: string | null; refCode: string | null } | null;
  /** false бол бичилтийн сар хаагдсан — урвуу бичилт хийх боломжгүй. */
  periodOpen: boolean;
};

export interface IJournalSummary {
  entries: number;
  totalDebit: number;
  totalCredit: number;
  balanced: boolean;
  reversals: number;
  /** Source шүүлтийг үл тооцсон — табын тоо. */
  bySource: Record<string, number>;
}

export interface ILedgerCoverage {
  unpostedInvoices: number;
  unpostedPayments: number;
  cancelledNotReversed: number;
  firstUnpostedDate: string | null;
  openingCutoff: string | null;
}

export interface IJournalResponse {
  summary: IJournalSummary;
  entries: IJournalEntry[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export interface IBusinessBalance {
  businessId: string;
  businessName: string;
  totalDebit: number;
  totalCredit: number;
  accounts: ILedgerAccount[];
  controls: IControlCheck[];
  ar: IARReconciliation;
}

export interface IVerifyBalanceReport {
  checkedAt: string;
  entriesChecked: number;
  unbalanced: { entryNumber: string; totalDebit: number; totalCredit: number }[];
  businesses: IBusinessBalance[];
  ok: boolean;
}

export const getAccounts = async () =>
  httpRequest.get<IAccountsResponse>("/inv/ledger/accounts");

export const getTemplates = async () =>
  httpRequest.get<IPostingTemplate[]>("/inv/ledger/templates");

export const saveTemplate = async (id: string, body: {
  name?: string;
  isActive?: boolean;
  lines?: IPostingTemplateLine[];
}) =>
  // The request helper's JSON type cannot express an array of typed objects;
  // the shape is validated server-side before anything is stored.
  httpRequest.put<IPostingTemplate>(
    `/inv/ledger/templates/${id}`,
    JSON.parse(JSON.stringify(body)),
  );

export type JournalParams = {
  dateFrom?: string;
  dateTo?: string;
  sourceType?: string;
  status?: string;
  /** Бичилтийн дугаар, тайлбар, эх нэхэмжлэхийн дугаар. */
  search?: string;
  /** Энэ дансны мөртэй бичилтүүд. */
  accountId?: string;
  page?: number;
  limit?: number;
};

export const getJournal = async (params?: JournalParams) =>
  httpRequest.get<IJournalResponse>("/inv/ledger/journal", params ?? {});

/** Дэвтэрт ороогүй үйл явдал — бизнесийн бүх хугацаагаар. */
export const getCoverage = async () => httpRequest.get<ILedgerCoverage>("/inv/ledger/coverage");

/** CSV — binary тул `.get` биш `.download` (HttpHandler-ийн parse-аар мушгирахгүй). */
export const exportJournalCsv = async (params?: Omit<JournalParams, "page" | "limit">): Promise<Blob> =>
  httpRequest.download("/inv/ledger/journal-export", params ?? {});

export const getEntry = async (entryId: string) =>
  httpRequest.get<IJournalEntryDetail>(`/inv/ledger/journal/${entryId}`);

/** Урвуу бичилт — эх бичилт устахгүй, REVERSED болно. */
export const reverseEntry = async (entryId: string, body?: { entryDate?: string; description?: string }) =>
  httpRequest.post<IJournalEntryDetail>(`/inv/ledger/journal/${entryId}/reverse`, body ?? {});

export const getVerifyReport = async () =>
  httpRequest.get<IVerifyBalanceReport>("/inv/ledger/verify");

// ── SPRINT-04 — Үйл явдал → дэвтэр ──────────────────────────────────────────

export type IInboxStatus = "RECEIVED" | "POSTED" | "SKIPPED" | "PENDING_COST" | "FAILED";

export interface IInboxEvent {
  id: string;
  /** Producer-ийн outbox мөрийн id. Дахин илгээхэд ижил утга. */
  eventId: string;
  eventType: string;
  aggregateType: string | null;
  aggregateId: string | null;
  occurredAt: string;
  status: IInboxStatus;
  entryId: string | null;
  templateCode: string | null;
  retryCount: number;
  /**
   * SKIPPED мөрд шалтгаан, FAILED мөрд алдаа. Алгасах нь алдаа БИШ —
   * тухайн үйл явдалд дансны бичилт хийх дүрэм байхгүй гэсэн үг.
   */
  lastError: string | null;
  processedAt: string | null;
  createdAt: string;
  /** Уншигдахуйц эх сурвалж — DN-100612, ST.AD-100031 гэх мэт. */
  sourceRef: string | null;
  /** POSTED мөрийн журналын дугаар (жагсаалтад л ирнэ). */
  entryNumber?: string | null;
}

export interface IInboxCounts {
  received: number;
  posted: number;
  skipped: number;
  pendingCost: number;
  failed: number;
  /** 7 хоногоос дээш өртөг хүлээсэн — DLQ болох дээр байгаа мөрүүд. */
  overdueCost: number;
}

export interface IInboxResponse {
  events: IInboxEvent[];
  counts: IInboxCounts;
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export type IInboxEventDetail = IInboxEvent & {
  payload: Record<string, unknown>;
  entry: IJournalEntryDetail | null;
};

export const getInboxEvents = async (params?: {
  dateFrom?: string;
  dateTo?: string;
  eventType?: string;
  status?: string;
  page?: number;
  limit?: number;
}) => httpRequest.get<IInboxResponse>("/inv/ledger/inbox", params ?? {});

export const getInboxEvent = async (inboxId: string) =>
  httpRequest.get<IInboxEventDetail>(`/inv/ledger/inbox/${inboxId}`);

/** Зөвхөн POSTED биш мөрд. Producer-ийн outbox мөр хөндөгдөхгүй. */
export const replayInboxEvent = async (inboxId: string) =>
  httpRequest.post<{ status: IInboxStatus; event: IInboxEventDetail }>(
    `/inv/ledger/inbox/${inboxId}/replay`,
    {},
  );

// ── SPRINT-05 — Санхүүгийн тайлан ────────────────────────────────────────────

export type LedgerConfigMode = "OFF" | "INTERNAL";

export interface ILedgerConfig {
  mode: LedgerConfigMode;
  enabledAt: string | null;
  disabledAt: string | null;
}

/** Task 11 Ruling 1 — the only place the nav's ledger gate reads the mode from. */
export const getLedgerConfig = async () =>
  httpRequest.get<ILedgerConfig>("/inv/ledger/config");

// ── SPRINT-09 Алхам 1 — дэвтэр асаах хаалга ──────────────────────────────────

/**
 * The PUT answers with the GET's shape plus `accountsEnsured` — how many
 * SYSTEM/CONTROL accounts the business ended up with. It is a count, not a
 * "created" count: `bootstrapAccounts` is idempotent, so switching an already
 * INTERNAL business on again reports the same number and creates nothing.
 */
export interface ISetLedgerConfigResult extends ILedgerConfig {
  accountsEnsured: number;
}

/**
 * Turns the internal ledger on or off for the signed-in business.
 *
 * OFF never deletes an account or an entry — it only stops new postings, so
 * turning it back on finds the ledger exactly as it was left.
 */
export const setLedgerConfig = async (mode: LedgerConfigMode) =>
  httpRequest.put<ISetLedgerConfigResult>("/inv/ledger/config", { mode });

export interface IPeriodBalanceRow {
  accountId: string;
  code: string;
  name: string;
  role: string;
  accountType: string;
  normalBalance: string;
  openingBalance: number;
  periodDebit: number;
  periodCredit: number;
  closingBalance: number;
  entryCount: number;
}

export interface ITrialBalanceReport {
  year: number;
  month: number;
  rows: IPeriodBalanceRow[];
  totalDebit: number;
  totalCredit: number;
  isBalanced: boolean;
  difference: number;
}

export interface IReportLine {
  lineCode: string;
  lineName: string;
  section: string;
  sortOrder: number;
  isSubtotal: boolean;
  amount: number;
  accounts: string[];
}

export interface IBalanceSheetReport {
  year: number;
  month: number;
  lines: IReportLine[];
  assets: number;
  liabilities: number;
  equity: number;
  periodProfit: number;
  isBalanced: boolean;
  difference: number;
  /**
   * Account rows in the period's balance snapshot. 0 means the period has no
   * balances at all — `lines` still arrives fully populated at 0 from
   * `report_line_map`, and `0 = 0 + 0` makes `isBalanced` true, so this is
   * the only way the screen can tell "nothing to report" from "balanced".
   */
  accountCount: number;
  unmapped: IPeriodBalanceRow[];
}

/**
 * Two sets of figures, both real. `revenue` / `expense` / `grossProfit` /
 * `netProfit` — and every `lines` amount — are THIS PERIOD's movement, which
 * is what the report's header dates and what the trial balance beside it
 * shows. The `ytd*` pair is the same arithmetic accumulated since the last
 * year-end close, which is what equity on the balance sheet carries.
 */
export interface IIncomeStatementReport {
  year: number;
  month: number;
  lines: IReportLine[];
  revenue: number;
  expense: number;
  grossProfit: number;
  netProfit: number;
  ytdRevenue: number;
  ytdExpense: number;
  ytdGrossProfit: number;
  ytdNetProfit: number;
  /**
   * Account rows in the period's balance snapshot. 0 means the period has no
   * balances at all — `lines` comes from `report_line_map` and arrives fully
   * populated at 0 either way, so gating a screen on `lines.length` is
   * gating on a constant.
   */
  accountCount: number;
  unmapped: IPeriodBalanceRow[];
}

export interface IArAgingCustomer {
  buyerBusinessId: string;
  buyerName: string;
  /** de_business.business.ref_code (BU-xxxxxx); null for an unknown buyer. */
  buyerRefCode?: string | null;
  currentBalance: number;
  bucketCurrent: number;
  bucket1to30: number;
  bucket31to60: number;
  bucket61to90: number;
  bucket91Plus: number;
  dso: number;
}

export interface IArAgingReport {
  customers: IArAgingCustomer[];
  subledgerTotal: number;
  controlBalance: number;
  difference: number;
}

export interface IStatementRow {
  entryId: string;
  entryNumber: string;
  entryDate: string;
  description: string | null;
  sourceType: string;
  isDebit: boolean;
  amount: number;
  balance: number;
}

export interface IAccountStatementReport {
  account: { accountId: string; code: string; name: string; normalBalance: string };
  dateFrom: string;
  dateTo: string;
  openingBalance: number;
  rows: IStatementRow[];
  closingBalance: number;
  truncated: boolean;
}

export type ReportPeriodParams = { year: number; month: number; fresh?: boolean };

export const getTrialBalance = async (params: ReportPeriodParams) =>
  httpRequest.get<ITrialBalanceReport>("/inv/ledger/report/trial-balance", params);

export const getBalanceSheet = async (params: ReportPeriodParams) =>
  httpRequest.get<IBalanceSheetReport>("/inv/ledger/report/balance-sheet", params);

export const getIncomeStatement = async (params: ReportPeriodParams) =>
  httpRequest.get<IIncomeStatementReport>("/inv/ledger/report/income-statement", params);

export const getArAging = async (params: { year: number; month: number }) =>
  httpRequest.get<IArAgingReport>("/inv/ledger/report/ar-aging", params);

export const getAccountStatement = async (params: {
  accountId: string;
  dateFrom: string;
  dateTo: string;
  limit?: number;
}) =>
  httpRequest.get<IAccountStatementReport>(
    `/inv/ledger/report/account/${params.accountId}`,
    { dateFrom: params.dateFrom, dateTo: params.dateTo, ...(params.limit ? { limit: params.limit } : {}) },
  );

export type ReportExportParams =
  | ({ report: "trial-balance" | "balance-sheet" | "income-statement" | "ar-aging" } & ReportPeriodParams)
  | { report: "account-statement"; accountId: string; dateFrom: string; dateTo: string; limit?: number };

/**
 * Excel — binary. Goes through `HttpRequest.download` (Ruling 2), never
 * `.get`, or the bytes come back mangled by `HttpHandler`'s JSON/text parse.
 */
export const exportReportExcel = async (params: ReportExportParams): Promise<Blob> =>
  httpRequest.download("/inv/ledger/report/export", { ...params, format: "excel" });

/**
 * PDF — the backend renders it server-side and hands back a URL to open.
 * Only `balance-sheet` and `income-statement` support this format; the
 * screen must not render a PDF button for the other three report kinds.
 */
export const exportReportPdf = async (params: ReportExportParams): Promise<{ url: string }> =>
  httpRequest.get<{ url: string }>("/inv/ledger/report/export", { ...params, format: "pdf" });

// ── SPRINT-05 Task 12 — periods and opening balance ──────────────────────────

export type LedgerPeriodStatus = "OPEN" | "CLOSING" | "CLOSED" | "LOCKED";

export interface ILedgerPeriod {
  id: string;
  businessId: string;
  year: number;
  month: number;
  startDate: string;
  endDate: string;
  status: LedgerPeriodStatus;
  closedAt: string | null;
  closedUserId: string | null;
  reopenedAt: string | null;
  reopenedUserId: string | null;
  reopenReason: string | null;
}

export interface IChecklistItem {
  code: string;
  label: string;
  ok: boolean;
  detail: string;
}

export interface IPeriodChecklist {
  period: ILedgerPeriod;
  items: IChecklistItem[];
  canClose: boolean;
}

export const getPeriods = async () =>
  httpRequest.get<ILedgerPeriod[]>("/inv/ledger/period");

export const getPeriodChecklist = async (periodId: string) =>
  httpRequest.get<IPeriodChecklist>(`/inv/ledger/period/${periodId}/checklist`);

export const closePeriod = async (periodId: string) =>
  httpRequest.post<ILedgerPeriod>(`/inv/ledger/period/${periodId}/close`, {});

/** The reason is mandatory server-side (`reopenPeriod` throws without one). */
export const reopenPeriod = async (periodId: string, reason: string) =>
  httpRequest.post<ILedgerPeriod>(`/inv/ledger/period/${periodId}/reopen`, { reason });

/**
 * The year-end close is repeatable, so its result says which of three things
 * happened:
 *  - POSTED       — the year's first closing entry
 *  - SUPPLEMENTED — a residual that landed after the year was closed, swept
 *                   by an additional entry
 *  - ALREADY_CLOSED — nothing left to sweep; the returned entry is the one
 *                   that did the work, and nothing was posted
 *
 * `closeStatus`, not `status`: the journal entry itself already carries a
 * `status` (POSTED / REVERSED).
 */
export type YearEndCloseStatus = "POSTED" | "SUPPLEMENTED" | "ALREADY_CLOSED";

export type IYearEndCloseResult = IJournalEntryDetail & {
  closeStatus: YearEndCloseStatus;
  /** Which closing entry of the year this is. 1 for the ordinary close. */
  sequence: number;
  /** The profit or loss this call closed out. 0 when nothing was posted. */
  netProfit: number;
};

/** Closes revenue/expense into retained earnings for a fiscal year. */
export const yearEndClose = async (year: number) =>
  httpRequest.post<IYearEndCloseResult>("/inv/ledger/period/year-end", { year });

export interface IOpeningRow {
  role: string;
  roleRef: string | null;
  debit: number;
  credit: number;
}

export interface IOpeningPreview {
  rows: IOpeningRow[];
  balancingRow: IOpeningRow | null;
}

export interface IOpeningImportResult {
  entryId: string;
  entryNumber: string;
  lines: number;
}

/**
 * Read-only — parses and balances the CSV without posting (Task 12 ruling).
 * Must be used to render the preview; never re-implement the parse in the
 * browser, or the numbers shown could disagree with what import posts.
 */
export const previewOpeningBalance = async (csv: string) =>
  httpRequest.post<IOpeningPreview>("/inv/ledger/opening/preview", { csv });

/** Posts a journal entry that cannot be repeated for the same activation. */
export const importOpeningBalance = async (csv: string) =>
  httpRequest.post<IOpeningImportResult>("/inv/ledger/opening", { csv });
// ── SPRINT-06 Алхам 11 — Mirror, intercompany, memo ─────────────────────────

export interface IMirrorSide {
  entryId: string;
  entryNumber: string;
  businessId: string;
  businessName: string;
  postingTemplate: string | null;
  totalDebit: number;
  totalCredit: number;
  /**
   * Балансын ГАДУУРХ тал. §8.1 үүнийг тусад нь заасан бөгөөд тэр ялгаа нь
   * консигнацийн гол санаа — memo мөрийг ердийн хөлтэй нийлүүлж болохгүй.
   */
  isMemo: boolean;
}

export interface IMirrorGroup {
  mirrorGroupId: string;
  entryDate: string;
  sourceType: string;
  sourceId: string | null;
  /** `CONSIGN_SOLD` — талын template кодоос гаргасан үйл явдал. */
  event: string | null;
  description: string | null;
  parties: { businessId: string; businessName: string }[];
  sides: IMirrorSide[];
  sideCount: number;
  /**
   * ХАМГИЙН ТОМ талын дүн. Mirror-д нэг «дүн» гэж байхгүй: нийлүүлэгч
   * авлага БА өртгөө бичдэг, эзэмшигч зөвхөн мөнгөө — гурван бодит тооны
   * алийг нь ч «тэр дүн» гэж хэлэх боломжгүй.
   */
  amount: number;
  isBalanced: boolean;
  createdAt: string;
}

export interface IMirrorResponse {
  groups: IMirrorGroup[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export interface IMirrorGroupLine extends IJournalLine {
  accountType: string;
}

export type IMirrorGroupEntry = IMirrorSide & {
  entryDate: string;
  sourceType: string;
  sourceId: string | null;
  description: string | null;
  status: string;
  lines: IMirrorGroupLine[];
};

export interface IMirrorGroupDetail {
  mirrorGroupId: string;
  entryDate: string;
  sourceType: string;
  sourceId: string | null;
  event: string | null;
  sides: IMirrorGroupEntry[];
  sideCount: number;
  amount: number;
  isBalanced: boolean;
}

interface IPairBase {
  businessId: string;
  businessName: string;
  counterpartyId: string;
  counterpartyName: string;
  diff: number;
}

export type IReceivablePair = IPairBase & { receivable: number; payable: number };
export type IIntercoPair = IPairBase & { balance: number; counterBalance: number };
export type IConsignmentPair = IPairBase & { ownerStock: number; holderMemo: number };

export interface ISkippedPair {
  businessId: string;
  counterpartyId: string;
  role: string;
  reason: string;
}

export interface IMirrorGroupRow {
  mirrorGroupId: string;
  entries: number;
  businessIds: string[];
  counterpartyIds: (string | null)[];
}

export interface IIntercompanyReport {
  receivablePairs: IReceivablePair[];
  intercoPairs: IIntercoPair[];
  consignmentPairs: IConsignmentPair[];
  lonelyMirrorGroups: IMirrorGroupRow[];
  /** Зориудаар хагас бичигдсэн — эсрэг тал дэвтэргүй (§0.2b). Алдаа БИШ. */
  unledgeredMirrorGroups: IMirrorGroupRow[];
  /** Нэг тал нь OFF тул шалгалтаас хасагдсан хос (A2b). Алдаа БИШ. */
  unledgeredPairs: ISkippedPair[];
  /** refCode нь ямар ч бизнестэй тааралгүй үлдсэн. `ok`-д нөлөөлөхгүй, ХАРАХ тоо. */
  unresolvedRefs: { businessId: string; role: string; roleRef: string }[];
  /**
   * refCode нь НЭГЭЭС ОЛОН бизнестэй таарсан — `ref_code`-д unique constraint
   * байхгүй. Нэрийг таамаглахын оронд аль нь ч сонгогдоогүй. `unresolvedRefs`-ээс
   * тусад нь: тэр нь дутуу бизнес, энэ нь код хуваалцсан хоёр бизнес.
   */
  ambiguousRefs: { businessId: string; role: string; roleRef: string; matches: number }[];
  commission: {
    expense: number;
    revenue: number;
    diff: number;
    /** Дэвтэргүй хос байвал false — комиссын нийлбэр шалгуур болохоо болино. */
    scopeComplete: boolean;
  };
  ok: boolean;
}

export interface IMemoRow {
  accountId: string;
  code: string;
  name: string;
  /** Өмчлөгчийн refCode. Нэр олдоогүй үед дэлгэц ҮҮНИЙГ харуулна. */
  vendorRef: string | null;
  vendorName: string | null;
  amount: number;
}

export interface IMemoReport {
  year: number;
  month: number;
  rows: IMemoRow[];
  total: number;
  contraTotal: number;
  isBalanced: boolean;
  difference: number;
  /** Нэг ref олон бизнестэй таарсан тул нэр нь ТОДОРХОЙЛОГДООГҮЙ мөрүүд. */
  ambiguousVendorRefs: { vendorRef: string; matches: number }[];
}

export const getMirrorGroups = async (params?: {
  dateFrom?: string;
  dateTo?: string;
  event?: string;
  page?: number;
  limit?: number;
}) => httpRequest.get<IMirrorResponse>("/inv/ledger/mirror", params ?? {});

export const getMirrorGroup = async (mirrorGroupId: string) =>
  httpRequest.get<IMirrorGroupDetail>(`/inv/ledger/mirror/${mirrorGroupId}`);

/** Хоёулаа INTERNAL хосуудын 5 шалгалт, энэ бизнесийн талаас харсан. */
export const getIntercompany = async () =>
  httpRequest.get<IIntercompanyReport>("/inv/ledger/intercompany");

export const getMemoAccounts = async (params: ReportPeriodParams) =>
  httpRequest.get<IMemoReport>("/inv/ledger/report/memo", params);
